import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { trackPageView, trackSignupCompleted } from '../utils/analytics';

const SignupSuccess = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    // Get URL parameters
    const emailParam = searchParams.get('email') || '';
    const nameParam = searchParams.get('name') || '';
    
    setEmail(emailParam);
    setName(nameParam);

    // Track page view
    trackPageView('/signup-success', 'Signup Success');
    
    // Track signup completion
    trackSignupCompleted({
      email: emailParam,
      name: nameParam
    });
  }, [searchParams]);

  return (
    <div className="pt-16 min-h-screen bg-gray-900">
      {/* Success Header */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Success Icon */}
            <div className="mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="w-20 h-20 mx-auto bg-secondary-600 rounded-full flex items-center justify-center"
              >
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Thank You for Signing Up!
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl text-gray-300 mb-8">
              We will let you know once we release the app to production
            </p>

            {/* Personal Greeting */}
            {name && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg text-secondary-400 mb-6"
              >
                Hi {name}! 👋
              </motion.p>
            )}

            {/* Email Confirmation */}
            {email && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-gray-400 mb-8"
              >
                We've sent a confirmation to <span className="text-secondary-400">{email}</span>
              </motion.p>
            )}

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <a
                href="https://expensight.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 text-lg"
              >
                Visit Our Website
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer Links */}
      <section className="py-12 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="text-center"
          >
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link
                to="/"
                className="text-secondary-400 hover:text-secondary-300 transition-colors duration-200"
              >
                Home
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                to="/pricing"
                className="text-secondary-400 hover:text-secondary-300 transition-colors duration-200"
              >
                Pricing
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                to="/contact"
                className="text-secondary-400 hover:text-secondary-300 transition-colors duration-200"
              >
                Contact
              </Link>
              <span className="text-gray-600">•</span>
              <a
                href="https://expensight.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-400 hover:text-secondary-300 transition-colors duration-200"
              >
                Website
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SignupSuccess;